import { Link } from "@tanstack/react-router";
import type { BlogPost } from "@/lib/blog";

interface Props {
  posts: BlogPost[];
  lang: "en" | "es";
}

export function BlogIndex({ posts, lang }: Props) {
  const isEs = lang === "es";

  return (
    <main className="max-w-4xl mx-auto px-6 py-16">
      <header className="mb-12">
        <p className="text-xs uppercase tracking-widest text-primary mb-3 font-semibold">
          {isEs ? "Blog de AdKavi" : "AdKavi Blog"}
        </p>
        <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
          {isEs ? "Perspectivas sobre publicidad con IA" : "Insights on AI Advertising"}
        </h1>
        <p className="text-lg text-muted-foreground max-w-xl">
          {isEs
            ? "Estrategias, actualizaciones de productos y reflexiones del equipo de AdKavi."
            : "Strategy, product updates, and thought leadership from the AdKavi team."}
        </p>
      </header>

      {posts.length === 0 ? (
        <p className="text-muted-foreground">
          {isEs ? "Próximamente — vuelve pronto." : "Coming soon — check back shortly."}
        </p>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2">
          {posts.map((post) => (
            <BlogCard key={post.slug} post={post} lang={lang} />
          ))}
        </div>
      )}

      <div className="mt-12 pt-8 border-t border-border/40">
        <Link
          to={isEs ? "/blog" : "/es/blog"}
          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          {isEs ? "Read in English →" : "Leer en español →"}
        </Link>
      </div>
    </main>
  );
}

function BlogCard({ post, lang }: { post: BlogPost; lang: "en" | "es" }) {
  const to = lang === "en" ? "/blog/$slug" : "/es/blog/$slug";

  return (
    <Link
      to={to}
      params={{ slug: post.slug }}
      className="group flex flex-col rounded-2xl border border-border/50 bg-card/40 hover:bg-card/70 overflow-hidden transition-colors"
    >
      {post.ogImage && (
        <div className="aspect-video overflow-hidden">
          <img
            src={post.ogImage}
            alt={post.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>
      )}
      <div className="flex flex-col flex-1 p-6">
        <time className="text-xs text-muted-foreground">
          {new Date(post.date + "T00:00:00").toLocaleDateString(
            lang === "en" ? "en-US" : "es-ES",
            { year: "numeric", month: "long", day: "numeric" },
          )}
        </time>
        <h2 className="mt-2 text-lg font-bold text-foreground leading-snug mb-2 group-hover:text-primary transition-colors">
          {post.title}
        </h2>
        <p className="text-sm text-muted-foreground line-clamp-3 flex-1">{post.description}</p>
        <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary">
          {lang === "en" ? "Read more" : "Leer más"}{" "}
          <span className="group-hover:translate-x-1 transition-transform inline-block">→</span>
        </span>
      </div>
    </Link>
  );
}
